import { TriangleAlert } from "lucide-react";
import { useState, type ReactNode } from "react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogFooter } from "@/components/ui/overlay";
import { Input } from "@/components/ui/primitives";

/**
 * Confirmação de ação sem volta (excluir projeto, grupo, conversa…). Com `confirmWord`, a pessoa
 * digita a palavra (o nome) para liberar o botão — como no GitHub.
 */
export function ConfirmDialog({
  open,
  onOpenChange,
  title,
  description,
  actionLabel,
  confirmWord,
  pending = false,
  danger = true,
  onConfirm,
  children,
}: {
  open: boolean;
  onOpenChange(open: boolean): void;
  title: string;
  description: ReactNode;
  actionLabel: string;
  confirmWord?: string;
  pending?: boolean;
  danger?: boolean;
  onConfirm(): void;
  children?: ReactNode;
}) {
  const [typed, setTyped] = useState("");
  const ready = !confirmWord || typed.trim() === confirmWord;
  return (
    <Dialog
      open={open}
      onOpenChange={(v) => {
        if (!v) setTyped("");
        onOpenChange(v);
      }}
    >
      <DialogContent title={title}>
        <div className="flex gap-3">
          {danger ? (
            <span className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-[var(--danger)]/12 text-[var(--danger)]">
              <TriangleAlert className="h-4 w-4" />
            </span>
          ) : null}
          <div className="min-w-0 space-y-3 text-[13px] leading-relaxed text-fg-muted">
            <div>{description}</div>
            {children}
            {confirmWord ? (
              <label className="block space-y-1.5">
                <span className="text-[12px]">
                  Digite <span className="font-mono font-semibold text-fg">{confirmWord}</span> para confirmar
                </span>
                <Input
                  value={typed}
                  onChange={(e) => setTyped(e.target.value)}
                  autoFocus
                  autoComplete="off"
                  spellCheck={false}
                  aria-label="Confirmação"
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && ready && !pending) onConfirm();
                  }}
                />
              </label>
            ) : null}
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            Cancelar
          </Button>
          <Button
            variant={danger ? "danger" : "primary"}
            disabled={!ready}
            loading={pending}
            onClick={onConfirm}
          >
            {actionLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
